import React, { useEffect, useState } from "react";
import { listScheduled } from "../api";

const STATE_STYLE = {
  ACTIVE:                   { bg: "#dcfce7", color: "#166534" },
  COMPLETED:                { bg: "#dcfce7", color: "#166534" },
  SCHEDULED:                { bg: "#dbeafe", color: "#1e40af" },
  INITIATED:                { bg: "#dbeafe", color: "#1e40af" },
  ACTIVATION_IN_PROGRESS:   { bg: "#dbeafe", color: "#1e40af" },
  PENDING:                  { bg: "#dbeafe", color: "#1e40af" },
  IN_PROGRESS:              { bg: "#fff7ed", color: "#c2410c" },
  NOTIFICATION_IN_PROGRESS: { bg: "#fff7ed", color: "#c2410c" },
  PAUSED:                   { bg: "#fef9c3", color: "#854d0e" },
  FAILED:                   { bg: "#fee2e2", color: "#991b1b" },
  CANCELLED:                { bg: "#f1f5f9", color: "#475569" },
  REVOKED:                  { bg: "#f1f5f9", color: "#475569" },
};

function Badge({ state }) {
  const style = STATE_STYLE[state] || { bg: "#f1f5f9", color: "#475569" };
  return <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600, backgroundColor: style.bg, color: style.color }}>{state}</span>;
}

function AttemptDots({ attempts, max = 3 }) {
  const n = Math.min(attempts || 0, max);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {Array.from({ length: max }).map((_, i) => (
        <span key={i} style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: i < n ? "#ef4444" : "#e2e8f0", display: "inline-block" }} />
      ))}
      <span style={{ marginLeft: 4, fontSize: 12, color: "#64748b" }}>{attempts}</span>
    </span>
  );
}

function fmtAmount(paise) {
  return `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}

function fmt(epochMs) {
  if (!epochMs) return "—";
  return new Date(epochMs).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(val) {
  if (!val) return "—";
  return new Date(val).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function SubscriptionDetailsModal({ appId, subscription, onClose, zIndex = 1000 }) {
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listScheduled(appId, { subscription_id: subscription.id, limit: 200 })
      .then(setScheduled)
      .finally(() => setLoading(false));
  }, [appId, subscription.id]);

  return (
    <div style={{ ...m.overlay, zIndex }} onClick={onClose}>
      <div style={m.box} onClick={(e) => e.stopPropagation()}>
        <div style={m.header}>
          <h2 style={m.title}>Subscription Details</h2>
          <button onClick={onClose} style={m.closeBtn}>✕</button>
        </div>

        <div style={m.grid}>
          <div style={m.field}><span style={m.label}>Subscription ID</span><code style={m.value}>{subscription.id}</code></div>
          <div style={m.field}><span style={m.label}>State</span><Badge state={subscription.state} /></div>
          <div style={m.field}><span style={m.label}>Phone Number</span><span style={m.value}>{subscription.phone_number}</span></div>
          <div style={m.field}><span style={m.label}>Trial Amount</span><span style={m.value}>{subscription.trail_days > 0 ? `${fmtAmount(subscription.amount)} (${subscription.trail_days}d)` : "—"}</span></div>
          <div style={m.field}><span style={m.label}>Max Amount</span><span style={m.value}>{subscription.max_amount ? fmtAmount(subscription.max_amount) : "—"}</span></div>
          <div style={m.field}><span style={m.label}>Amount Type</span><span style={m.value}>{subscription.amount_type}</span></div>
          <div style={m.field}><span style={m.label}>Frequency</span><span style={m.value}>{subscription.frequency}</span></div>
          <div style={m.field}><span style={m.label}>Auth Workflow</span><span style={m.value}>{subscription.auth_workflow_type}</span></div>
          <div style={m.field}><span style={m.label}>Product Type</span><span style={m.value}>{subscription.product_type}</span></div>
          <div style={m.field}><span style={m.label}>PhonePe Subscription ID</span><code style={m.value}>{subscription.phonepe_subscription_id || "—"}</code></div>
          <div style={m.field}><span style={m.label}>PhonePe Order ID</span><code style={m.value}>{subscription.phonepe_order_id || "—"}</code></div>
          <div style={m.field}><span style={m.label}>Next Payment</span><span style={m.value}>{fmt(subscription.next_payment_date)}</span></div>
          <div style={m.field}><span style={m.label}>Last Payment Status</span><span style={m.value}>{subscription.last_payment_status || "—"}</span></div>
          <div style={m.field}><span style={m.label}>Total Payments</span><span style={m.value}>{subscription.total_payments}</span></div>
          <div style={m.field}><span style={m.label}>Trail Days</span><span style={m.value}>{subscription.trail_days}</span></div>
          <div style={m.field}><span style={m.label}>Created</span><span style={m.value}>{fmtDateTime(subscription.created_at)}</span></div>
        </div>

        <h3 style={m.subheading}>Scheduled Transactions</h3>
        {loading ? (
          <div style={{ color: "#64748b", padding: 16 }}>Loading…</div>
        ) : scheduled.length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8", fontSize: 13 }}>No scheduled transactions yet.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={tbl.table}>
              <thead>
                <tr>
                  {["Payment Date", "Transaction ID", "Amount", "State", "Attempts", "Created"].map((h) => (
                    <th key={h} style={tbl.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scheduled.map((sc, i) => (
                  <tr key={sc.id} style={i % 2 !== 0 ? { backgroundColor: "#fafafa" } : {}}>
                    <td style={tbl.td}>{fmt(sc.payment_date)}</td>
                    <td style={tbl.td}>
                      {sc.transaction_id ? (
                        <code style={{ fontSize: 11, backgroundColor: "#f1f5f9", padding: "2px 6px", borderRadius: 4, fontFamily: "monospace", whiteSpace: "nowrap" }} title={sc.transaction_id}>{sc.transaction_id}</code>
                      ) : sc.merchant_order_id ? (
                        <code style={{ fontSize: 11, backgroundColor: "#f1f5f9", padding: "2px 6px", borderRadius: 4, fontFamily: "monospace", whiteSpace: "nowrap", color: "#94a3b8" }} title={`Merchant order ID (PhonePe txn ID not yet available): ${sc.merchant_order_id}`}>{sc.merchant_order_id}</code>
                      ) : "—"}
                    </td>
                    <td style={tbl.td}>{fmtAmount(sc.amount)}</td>
                    <td style={tbl.td}><Badge state={sc.state} /></td>
                    <td style={tbl.td}><AttemptDots attempts={sc.attempts} /></td>
                    <td style={tbl.td}>{fmtDateTime(sc.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const m = {
  overlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  box: { backgroundColor: "#fff", borderRadius: 12, padding: 28, width: "100%", maxWidth: 720, maxHeight: "90vh", overflowY: "auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  title: { margin: 0, fontSize: 18, color: "#1e293b" },
  closeBtn: { background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#64748b" },
  grid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14, marginBottom: 24 },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" },
  value: { fontSize: 13, color: "#1e293b", wordBreak: "break-all" },
  subheading: { fontSize: 14, fontWeight: 600, color: "#1e293b", margin: "0 0 12px" },
};

const tbl = {
  table: { width: "100%", borderCollapse: "collapse", backgroundColor: "#fff", fontSize: 13 },
  th: { padding: "10px 14px", textAlign: "left", backgroundColor: "#f8fafc", color: "#64748b", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" },
  td: { padding: "11px 14px", color: "#1e293b", borderBottom: "1px solid #f8fafc", verticalAlign: "middle" },
};
